.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  padding: var(--space-4);
  min-width: 0;
}

/* Headings inside cards wrap instead of running into the border. */
.card > h2,
.card > h3 {
  overflow-wrap: anywhere;
  hyphens: auto;
}

/*
 * A label that is wider than the space left for the button wraps inside the button; a single word
 * longer than the button (German compounds at 320 px) is hyphenated or broken instead of
 * overflowing. `break-word` keeps the longest word as the minimum width, so table columns with
 * buttons do not get narrower than before.
 */
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-width: var(--touch-target);
  min-height: var(--touch-target);
  padding: var(--space-2) var(--space-4);
  text-align: center;
  font-weight: 500;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius);
  background: var(--color-surface);
  cursor: pointer;
  text-decoration: none;
  color: var(--color-text);
  overflow-wrap: break-word;
  hyphens: auto;
}

.button:active:not(:disabled, [aria-disabled="true"]) {
  filter: brightness(0.95);
}

/* Full-width button (single actions on phones, account screen). */
.block {
  display: flex;
  width: 100%;
}

/* `aria-disabled` keeps a button focusable while it waits, for example during an NFC reading (ADR-0211). */
.button:disabled,
.button[aria-disabled="true"] {
  cursor: not-allowed;
  opacity: 0.6;
}

.primary {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: var(--color-accent-contrast);
  font-weight: 600;
}

.danger {
  background: var(--color-danger);
  border-color: var(--color-danger);
  color: var(--color-surface);
  font-weight: 600;
}

/*
 * Removing or archiving where a red filled button would shout (one per table row): red text and
 * outline on the surface; the confirmation keeps the filled danger button (F-29).
 */
.quietDanger {
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.alert {
  border-radius: var(--radius);
  padding: var(--space-3) var(--space-4);
  border-left: 4px solid;
}

.alertInfo {
  background: var(--color-info-surface);
  border-color: var(--color-accent);
}

.alertWarning {
  background: var(--color-warning-surface);
  border-color: var(--color-warning);
}

.alertDanger {
  background: var(--color-danger-surface);
  border-color: var(--color-danger);
}

.alertSuccess {
  background: var(--color-success-surface);
  border-color: var(--color-success);
}

.muted {
  color: var(--color-text-muted);
}

/* A done mark (✓) in the success colour; a pair of the contrast test on the surface (F-30). */
.successText {
  color: var(--color-success);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.field > span:first-child {
  font-weight: 500;
}

.field input,
.field select,
.field textarea {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius);
  background: var(--color-surface);
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: flex-end;
}

/*
 * A list entry with a text and its actions: centred, so the text stands level with the buttons
 * instead of on their bottom edge, which suits rows of fields with labels above (F-57). Doubled
 * class, so it also wins over the stretched rows of phones below; the buttons still share the
 * height of their line there.
 */
.row.itemRow {
  align-items: center;
}

/*
 * Phones: buttons in a row start from the width of their label and share the remaining space, and
 * fields use the full width, so rows end flush on both sides instead of leaving a ragged right
 * edge. A button never gets narrower than its label: if the buttons do not fit next to each other
 * (or next to a text in the row), the row wraps; only a button that is wider than the whole row on
 * its own wraps its label (UX-101, WCAG 1.4.10).
 */
@media (max-width: 719.98px) {
  .row > .button {
    flex: 1 1 auto;
  }

  .row > .field {
    flex: 1 1 100%;
  }
}

/* Buttons of equal width side by side (confirmations, period navigation). */
.buttonGrid {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: var(--space-3);
}

/* Form fields: one column on phones, two equal columns on wide screens. */
.formGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 720px) {
  .formGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  text-align: left;
  padding: var(--space-2);
  border-bottom: 1px solid var(--color-border);
  vertical-align: top;
}

/* Header row like the DataTable: raised surface, muted smaller text (design-guidelines.md 4, F-30). */
.table thead th {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-muted);
  background: var(--color-surface-raised);
}

/*
 * Horizontally scrollable region around a wide table. Positioned, so visually hidden content inside
 * (column headers such as „Aktionen“) is placed relative to the region and cannot widen the page
 * beyond the viewport (WCAG 1.4.10 Reflow).
 */
.tableScroll {
  position: relative;
  overflow-x: auto;
}

/* Numbers line up on the right; `.table .numeric` outranks the left alignment of `.table td`. */
.numeric,
.table .numeric {
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.segmented {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  width: 100%;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius);
  overflow: hidden;
}

@media (min-width: 720px) {
  .segmented {
    width: auto;
    justify-self: start;
    align-self: flex-start;
  }
}

.segmented button {
  border: 0;
  border-radius: 0;
  background: var(--color-surface);
  padding: 0 var(--space-4);
  cursor: pointer;
}

.segmented button + button {
  border-left: 1px solid var(--color-border-strong);
}

.segmented button[aria-pressed="true"] {
  background: var(--color-accent);
  color: var(--color-accent-contrast);
  font-weight: 600;
}

.badge {
  display: inline-block;
  padding: 0 var(--space-2);
  border-radius: 999px;
  background: var(--color-surface-raised);
  border: 1px solid var(--color-border);
  font-size: 0.875rem;
}

/*
 * Badge tones (F-29): each text colour on its tinted surface is a pair of the contrast test
 * (themeContrast.test.ts, at least 4.5:1 in both schemes). The words carry the meaning; the colour
 * only helps to scan (WCAG 1.4.1).
 */
.badgeSuccess,
.badgeInfo,
.badgeWarning,
.badgeDanger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-weight: 600;
}

.badgeSuccess {
  background: var(--color-success-surface);
  border-color: var(--color-success-surface);
  color: var(--color-success);
}

.badgeInfo {
  background: var(--color-info-surface);
  border-color: var(--color-info-surface);
  color: var(--color-accent);
}

.badgeWarning {
  background: var(--color-warning-surface);
  border-color: var(--color-warning-surface);
  color: var(--color-warning);
}

.badgeDanger {
  background: var(--color-danger-surface);
  border-color: var(--color-danger-surface);
  color: var(--color-danger);
}

/*
 * Disclosure: the summary is a flex row with a 44 px target, which drops the native marker. A
 * chevron drawn with borders takes its place (pointing to the text when closed, down when open), so
 * the summary does not look like plain text (UX-105). Element and state stay native, so keyboard
 * and screen reader behaviour is unchanged; the chevron is decorative (empty content).
 */
details > summary {
  cursor: pointer;
  min-height: var(--touch-target);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  list-style: none;
}

details > summary::-webkit-details-marker {
  display: none;
}

details > summary::before {
  content: "";
  flex: none;
  width: 0.5em;
  height: 0.5em;
  margin-inline: 0.125em 0.25em;
  border-right: 2px solid currentcolor;
  border-bottom: 2px solid currentcolor;
  transform: rotate(-45deg);
}

details[open] > summary::before {
  transform: rotate(45deg);
}

@media (prefers-reduced-motion: no-preference) {
  details > summary::before {
    transition: transform 0.15s ease-out;
  }
}

.tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.tabs a {
  width: 100%;
}

/* An odd last entry spans both columns, so the grid stays symmetric. */
.tabs > li:last-child:nth-child(odd) {
  grid-column: 1 / -1;
}

@media (min-width: 720px) {
  .tabs {
    display: flex;
    flex-wrap: wrap;
  }

  .tabs a {
    width: auto;
  }
}

.tabs a[aria-current="page"] {
  border-color: var(--color-accent);
  box-shadow: inset 0 -3px 0 var(--color-accent);
  font-weight: 600;
}

.checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
  border: 0;
  padding: 0;
  margin: 0;
}

.checkboxes label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-height: var(--touch-target);
}

/*
 * Checkbox with its label (shared Checkbox component, UX-104, UX-121): box and text in two grid
 * columns, so a long text wraps beside the box instead of below it, and the whole label is the
 * target, at least 44 px high (11.2, WCAG 2.5.8). The box is 24 px and stays aligned with the first
 * line of the text. The text column is at least as wide as the longest word, so the label also
 * keeps its text inside a table cell.
 *
 * Labels with the class `row` around a checkbox and its text get the same layout until every
 * screen uses the component.
 */
.checkbox,
label.row:has(> input[type="checkbox"], > input[type="radio"]) {
  display: grid;
  grid-template-columns: var(--checkbox-size) 1fr;
  align-items: start;
  align-content: center;
  gap: var(--space-3);
  min-height: var(--touch-target);
  padding-block: var(--space-2);
  cursor: pointer;
}

.checkbox > input,
label.row > input[type="checkbox"],
label.row > input[type="radio"] {
  width: var(--checkbox-size);
  height: var(--checkbox-size);
  margin: 0;
  accent-color: var(--color-accent);
  cursor: inherit;
}

.checkbox:has(> input:disabled) {
  color: var(--color-text-muted);
  cursor: not-allowed;
}

/*
 * Standalone link: a link (or a button that looks like one) on its own line or next to buttons,
 * for example "Zurück zur Liste" or "Korrektur für diesen Tag". It keeps the look of a link, but
 * its target is at least 44 × 44 px (11.2, WCAG 2.5.8, UX-104).
 *
 * Links inside running text do not get this class: their height follows the line height of the
 * sentence, which WCAG 2.5.8 exempts ("inline" exception), and extra height would tear the
 * paragraph apart.
 */
.linkButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-width: var(--touch-target);
  min-height: var(--touch-target);
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-accent);
  font: inherit;
  text-align: start;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  overflow-wrap: break-word;
  cursor: pointer;
}

.linkButton:disabled {
  color: var(--color-text-muted);
  cursor: not-allowed;
}

.periodLabel {
  text-align: center;
}

.buttonGrid .button {
  padding-inline: var(--space-2);
}

/* A card that is also a stack or form keeps its padding (the list reset of .stack comes later). */
.card.stack,
.card.row {
  padding: var(--space-4);
}

.card.stack {
  gap: var(--space-3);
}

/*
 * A fieldset that is also a stack keeps the inner spacing of fieldsets (global.css); the list reset
 * of .stack put its fields onto the frame (F-57).
 */
fieldset.stack {
  padding: var(--space-3) var(--space-4) var(--space-4);
}

@media (max-width: 719.98px) {
  /* Buttons in one row get the same height, even if one label wraps. */
  .row {
    align-items: stretch;
  }

  .row.itemRow > .button {
    align-self: stretch;
  }
}

/*
 * Shared feedback components (UX WP-2).
 *
 * Error code of ErrorMessage: kept on one line, so „TT-9999“ never breaks at its hyphen (UX-108).
 */
.errorCode {
  white-space: nowrap;
  font-size: 0.875rem;
}

.errorMessage > .row {
  margin-top: var(--space-2);
}

/*
 * Field (UX-111): label, hint, error and control. An invalid control gets a danger outline in
 * addition to the error text, so the state does not rely on colour alone.
 */
.fieldLabel {
  font-weight: 500;
}

.fieldOptional {
  font-weight: 400;
  color: var(--color-text-muted);
}

.fieldHint {
  margin: 0;
}

.fieldError {
  margin: 0;
  color: var(--color-danger);
  font-weight: 600;
}

.field [aria-invalid="true"] {
  border-color: var(--color-danger);
  box-shadow: 0 0 0 1px var(--color-danger);
}

/*
 * Error summary of a validated form (F-60, UX-008): the last child of the form, on a line of its
 * own in rows and grids.
 */
.errorSummary {
  flex: 1 1 100%;
  grid-column: 1 / -1;
}

/* Inline confirmation of a destructive or binding action (ConfirmAction, UX-113). */
.confirmAction {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/*
 * Skeleton (UX-117): bars with roughly the height of the final content; decorative, the loading
 * state is announced by a visually hidden live region.
 */
.skeletonLines,
.skeletonCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.skeletonItem {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-block: var(--space-3);
  border-bottom: 1px solid var(--color-border);
}

.skeletonBar {
  display: block;
  height: 1rem;
  border-radius: var(--radius);
  background: var(--color-border);
  opacity: 0.6;
}

.skeletonBar:last-child:not(:first-child) {
  width: 70%;
}

.skeletonTitle {
  width: 45%;
  height: 1.25rem;
}

.skeletonHeading {
  width: 40%;
  height: 1.5rem;
  margin-bottom: var(--space-2);
}

@media (prefers-reduced-motion: no-preference) {
  .skeletonBar {
    animation: skeleton-pulse 1.6s ease-in-out infinite;
  }
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 0.6;
  }

  50% {
    opacity: 0.3;
  }
}

/*
 * Toast of the announcer (UX-112): the live region is always in the document and only invisible
 * while empty. Outside the app shell (public pages) it sits at the bottom of the window, on phones
 * above the bottom navigation; inside the shell it sits at the top of the content area (F-61).
 */
.toastRegion {
  position: fixed;
  inset-inline: 0;
  bottom: calc(var(--safe-bottom) + var(--space-5));
  z-index: 40;
  display: flex;
  justify-content: center;
  padding-inline: var(--gutter);
  pointer-events: none;
}

@media (max-width: 719.98px) {
  .toastRegion {
    bottom: calc(var(--nav-bar-height) + var(--safe-bottom) + var(--space-3));
  }
}

/*
 * Inside the app shell the toast sits at the top of the content area (F-61): the anchor is the
 * first child of the main landmark, sticky below the app bar and without height, so nothing moves
 * when it appears and it never lies over the navigation or the bottom bar. At the top of a page it
 * lies over the heading; on a scrolled page it may lie over content for a few seconds, so it lets
 * every click through to what lies below (no pointer events). A control scrolled into that band with
 * the keyboard can stay hidden below it for up to six seconds (known limitation).
 */
.toastAnchor {
  position: sticky;
  top: calc(var(--safe-top) + var(--app-bar-height) + var(--space-2));
  z-index: 15;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 0;
  pointer-events: none;
}

.toastAnchor > .toast {
  pointer-events: none;
}

.toast {
  max-width: 32rem;
  border-radius: var(--radius);
  border-left-style: solid;
  border-left-width: 4px;
  padding: var(--space-3) var(--space-4);
  box-shadow: 0 4px 16px rgb(16 24 40 / 18%);
  font-weight: 500;
  opacity: 0;
  transform: translateY(var(--space-2));
}

.toast:empty {
  padding: 0;
  border: 0;
  box-shadow: none;
}

.toastVisible {
  opacity: 1;
  transform: none;
  pointer-events: auto;
}

@media (prefers-reduced-motion: no-preference) {
  .toast {
    transition:
      opacity 0.15s ease-out,
      transform 0.15s ease-out;
  }
}
